'use client';

import { c as _c } from "react/compiler-runtime";
import { useState, useRef, useSyncExternalStore } from 'react';
import dynamic from 'next/dynamic';
import AppDock from '@/components/AppDock';
import { MobileNav } from '@/components/closet/MobileNav';
import { ViewToggle } from '@/components/closet/ViewToggle';
import { ClosetView } from '@/components/closet/ClosetView';
import { LookbookCollection } from '@/components/closet/lookbook/LookbookCollection';
import { navigateLookbook, getClosetView, subscribeNavigation } from '@/components/closet/lookbook/navigation';
import { OutfitBuilder } from '@/components/closet/OutfitBuilder';
import { CategoryFilter } from '@/components/closet/CategoryFilter';
import { ViewLayoutToggle } from '@/components/closet/ViewLayoutToggle';
import { ZoomSlider } from '@/components/closet/ZoomSlider';
import { SoundToggle } from '@/components/closet/SoundToggle';
import { useViewPreferences } from '@/hooks/useViewPreferences';
import { useWebHaptics } from 'web-haptics/react';
import { useSound } from '@/hooks/useSound';
import { useIsMobile } from '@/hooks/useIsMobile';
import { loginToCloset, readClosetAuthToken } from '@/lib/closet/client-auth';
import type { Outfit } from '@/types/closet';

// Lazy load modal - only appears on user interaction
const AddFromUrlModal = dynamic(() => import('@/components/closet/AddFromUrlModal').then(mod => mod.AddFromUrlModal));
type ViewMode = 'closet' | 'outfits' | 'create';
export default function ClosetPage(t0) {
  const $ = _c(26);
  const {
    initialOutfits,
    initialView: t1,
    initialOutfitId
  } = t0;
  const initialView = t1 === undefined ? "closet" : t1;
  const closet = useClosetPageController(initialView);
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <h1 className="sr-only">Digital Closet</h1>;
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  let t3;
  let t4;
  if ($[1] !== closet) {
    t3 = <ClosetTopNav closet={closet} />;
    t4 = <ClosetMobileModeControl closet={closet} />;
    $[1] = closet;
    $[2] = t3;
    $[3] = t4;
  } else {
    t3 = $[2];
    t4 = $[3];
  }
  let t5;
  if ($[4] !== closet || $[5] !== initialOutfitId || $[6] !== initialOutfits) {
    t5 = <ClosetMainViews closet={closet} initialOutfits={initialOutfits} initialOutfitId={initialOutfitId} />;
    $[4] = closet;
    $[5] = initialOutfitId;
    $[6] = initialOutfits;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  let t7;
  if ($[8] !== closet) {
    t6 = <ClosetBottomControls closet={closet} />;
    t7 = <ClosetNavigationChrome closet={closet} />;
    $[8] = closet;
    $[9] = t6;
    $[10] = t7;
  } else {
    t6 = $[9];
    t7 = $[10];
  }
  let t8;
  if ($[11] !== closet) {
    t8 = () => closet.setShowAddModal(false);
    $[11] = closet;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  let t9;
  if ($[13] !== closet.showAddModal || $[14] !== t8) {
    t9 = <AddFromUrlModal isOpen={closet.showAddModal} onClose={t8} />;
    $[13] = closet.showAddModal;
    $[14] = t8;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  let t10;
  if ($[16] !== closet) {
    t10 = <ClosetPasswordPrompt closet={closet} />;
    $[16] = closet;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  let t11;
  if ($[18] !== t10 || $[19] !== t3 || $[20] !== t4 || $[21] !== t5 || $[22] !== t6 || $[23] !== t7 || $[24] !== t9) {
    t11 = <div className="relative h-screen w-full overflow-hidden bg-white dark:bg-neutral-950">{t2}{t3}{t4}{t5}{t6}{t7}{t9}{t10}</div>;
    $[18] = t10;
    $[19] = t3;
    $[20] = t4;
    $[21] = t5;
    $[22] = t6;
    $[23] = t7;
    $[24] = t9;
    $[25] = t11;
  } else {
    t11 = $[25];
  }
  return t11;
}
function useClosetPageController(initialView) {
  const $ = _c(45);
  const [creating, setCreating] = useState(false);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = [];
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const [savedOutfits, setSavedOutfits] = useState(t0);
  let t1;
  if ($[1] !== initialView) {
    t1 = () => initialView;
    $[1] = initialView;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const currentView = useSyncExternalStore(subscribeNavigation, getClosetView, t1);
  const viewMode = creating ? "create" : currentView;
  let t2;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = mode => {
      setCreating(mode === "create");
      if (mode !== "create") {
        navigateLookbook(undefined, mode === "closet" ? "closet" : undefined);
      }
    };
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const setViewMode = t2;
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [hoveredCategory, setHoveredCategory] = useState(null);
  const [searchFocused, setSearchFocused] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showPasswordPrompt, setShowPasswordPrompt] = useState(false);
  const [password, setPassword] = useState("");
  const [authError, setAuthError] = useState("");
  const {
    layout,
    zoom,
    soundEnabled,
    setLayout,
    setZoom,
    setSoundEnabled
  } = useViewPreferences();
  const haptic = useWebHaptics();
  const {
    playSound
  } = useSound(soundEnabled);
  const isMobile = useIsMobile();
  const searchTimeoutRef = useRef(null);
  let t3;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = value => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
      searchTimeoutRef.current = setTimeout(() => {
        setSearchQuery(() => value);
      }, 300);
    };
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const handleSearchChange = t3;
  let t4;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = () => {
      const existingToken = readClosetAuthToken();
      if (existingToken) {
        setShowAddModal(true);
      } else {
        setShowPasswordPrompt(true);
      }
    };
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const handleAddClick = t4;
  let t5;
  if ($[6] !== haptic || $[7] !== password) {
    t5 = async e => {
      e.preventDefault();
      setAuthError("");
      if (!password) {
        setAuthError("PASSWORD IS REQUIRED");
        return;
      }
      try {
        if (await loginToCloset(password)) {
          haptic.trigger("success");
          setShowPasswordPrompt(false);
          setShowAddModal(true);
          setPassword("");
        } else {
          haptic.trigger("error");
          setAuthError("INCORRECT PASSWORD");
        }
      } catch {
        haptic.trigger("error");
        setAuthError("CONNECTION ERROR");
      }
    };
    $[6] = haptic;
    $[7] = password;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const handlePasswordSubmit = t5;
  let t6;
  if ($[9] !== haptic || $[10] !== playSound) {
    t6 = () => {
      haptic.trigger("medium");
      playSound("click");
      setViewMode("create");
    };
    $[9] = haptic;
    $[10] = playSound;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  const createOutfit = t6;
  let t7;
  if ($[12] !== haptic || $[13] !== playSound || $[14] !== setLayout) {
    t7 = newLayout => {
      setLayout(newLayout);
      haptic.trigger("selection");
      playSound("switch");
    };
    $[12] = haptic;
    $[13] = playSound;
    $[14] = setLayout;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  const handleLayoutChange = t7;
  let t8;
  if ($[16] !== haptic || $[17] !== playSound) {
    t8 = outfit => {
      setSavedOutfits(saved => [...saved, outfit]);
      haptic.trigger("success");
      playSound("success");
      setViewMode("outfits");
    };
    $[16] = haptic;
    $[17] = playSound;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  const handleCreateSuccess = t8;
  let t9;
  if ($[19] !== playSound) {
    t9 = () => {
      playSound("click");
      setViewMode("outfits");
    };
    $[19] = playSound;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  const handleCreateBack = t9;
  let t10;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = () => {
      setShowPasswordPrompt(false);
      setPassword("");
      setAuthError("");
    };
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  const closePasswordPrompt = t10;
  let t11;
  if ($[22] !== authError || $[23] !== createOutfit || $[24] !== handleCreateBack || $[25] !== handleCreateSuccess || $[26] !== handleLayoutChange || $[27] !== handlePasswordSubmit || $[28] !== hoveredCategory || $[29] !== isMobile || $[30] !== layout || $[31] !== password || $[32] !== playSound || $[33] !== savedOutfits || $[34] !== searchFocused || $[35] !== searchQuery || $[36] !== selectedCategory || $[37] !== setSoundEnabled || $[38] !== setZoom || $[39] !== showAddModal || $[40] !== showPasswordPrompt || $[41] !== soundEnabled || $[42] !== viewMode || $[43] !== zoom) {
    t11 = {
      viewMode,
      searchQuery,
      selectedCategory,
      hoveredCategory,
      searchFocused,
      showAddModal,
      showPasswordPrompt,
      password,
      authError,
      layout,
      zoom,
      soundEnabled,
      playSound,
      isMobile,
      setViewMode,
      setSelectedCategory,
      setHoveredCategory,
      setSearchFocused,
      setShowAddModal,
      setPassword,
      setZoom,
      setSoundEnabled,
      handleSearchChange,
      handleAddClick,
      handlePasswordSubmit,
      createOutfit,
      handleLayoutChange,
      handleCreateSuccess,
      savedOutfits,
      handleCreateBack,
      closePasswordPrompt
    };
    $[22] = authError;
    $[23] = createOutfit;
    $[24] = handleCreateBack;
    $[25] = handleCreateSuccess;
    $[26] = handleLayoutChange;
    $[27] = handlePasswordSubmit;
    $[28] = hoveredCategory;
    $[29] = isMobile;
    $[30] = layout;
    $[31] = password;
    $[32] = playSound;
    $[33] = savedOutfits;
    $[34] = searchFocused;
    $[35] = searchQuery;
    $[36] = selectedCategory;
    $[37] = setSoundEnabled;
    $[38] = setZoom;
    $[39] = showAddModal;
    $[40] = showPasswordPrompt;
    $[41] = soundEnabled;
    $[42] = viewMode;
    $[43] = zoom;
    $[44] = t11;
  } else {
    t11 = $[44];
  }
  return t11;
}
type ClosetPageController = ReturnType<typeof useClosetPageController>;
function ClosetTopNav(t0) {
  const $ = _c(11);
  const {
    closet
  } = t0;
  if (closet.viewMode === "create") {
    return null;
  }
  const t1 = `pointer-events-auto flex min-h-[33px] items-center ${closet.viewMode === "closet" ? "flex-1" : ""}`;
  let t2;
  if ($[0] !== closet.setViewMode || $[1] !== closet.viewMode) {
    t2 = <ViewToggle viewMode={closet.viewMode} onViewChange={closet.setViewMode} />;
    $[0] = closet.setViewMode;
    $[1] = closet.viewMode;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== t1 || $[4] !== t2) {
    t3 = <div className={t1}>{t2}</div>;
    $[3] = t1;
    $[4] = t2;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== closet) {
    t4 = closet.viewMode === "closet" && <><div className={`ease-out-strong pointer-events-auto flex flex-1 justify-center transition-[width,opacity] duration-300 ${closet.searchFocused && closet.isMobile ? "hidden" : ""}`}>{!closet.isMobile && <ClosetModeAction closet={closet} />}</div><ClosetSearch closet={closet} /></>;
    $[6] = closet;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== t3 || $[9] !== t4) {
    t5 = <div className="pointer-events-none fixed top-0 right-0 left-0 z-40 flex items-center justify-between px-4 py-6 md:px-8 md:py-8">{t3}{t4}</div>;
    $[8] = t3;
    $[9] = t4;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  return t5;
}
function ClosetModeAction(t0) {
  const $ = _c(6);
  const {
    closet
  } = t0;
  if (closet.viewMode === "closet") {
    let t1;
    if ($[0] !== closet.selectedCategory || $[1] !== closet.setHoveredCategory || $[2] !== closet.setSelectedCategory) {
      t1 = <CategoryFilter selectedCategory={closet.selectedCategory} onCategoryChange={closet.setSelectedCategory} onCategoryHover={closet.setHoveredCategory} />;
      $[0] = closet.selectedCategory;
      $[1] = closet.setHoveredCategory;
      $[2] = closet.setSelectedCategory;
      $[3] = t1;
    } else {
      t1 = $[3];
    }
    return t1;
  }
  let t1;
  if ($[4] !== closet.createOutfit) {
    t1 = <button type="button" className="text-xs opacity-40 transition-opacity hover:opacity-100" onClick={closet.createOutfit}>MAKE NEW OUTFIT</button>;
    $[4] = closet.createOutfit;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  return t1;
}
function ClosetSearch(t0) {
  const $ = _c(14);
  const {
    closet
  } = t0;
  closet.searchFocused && closet.isMobile ? "flex-1" : "flex-1";
  const t1 = `ease-out-strong relative transition-[width,opacity] duration-300 ${closet.searchFocused && closet.isMobile ? "w-full" : ""}`;
  let t2;
  let t3;
  let t4;
  if ($[0] !== closet) {
    t2 = e => closet.handleSearchChange(e.target.value);
    t3 = () => closet.setSearchFocused(true);
    t4 = () => closet.setSearchFocused(false);
    $[0] = closet;
    $[1] = t2;
    $[2] = t3;
    $[3] = t4;
  } else {
    t2 = $[1];
    t3 = $[2];
    t4 = $[3];
  }
  const t5 = `ease-out-strong border-b border-zinc-300 bg-transparent px-2 py-1 text-xs transition-[width,border-color] duration-300 placeholder:text-zinc-400 focus:border-zinc-900 focus:outline-none dark:border-zinc-700 dark:placeholder:text-zinc-600 dark:focus:border-white ${closet.searchFocused && closet.isMobile ? "w-full" : closet.isMobile ? "w-32" : "w-48"}`;
  let t6;
  if ($[4] !== closet.searchQuery || $[5] !== t2 || $[6] !== t3 || $[7] !== t4 || $[8] !== t5) {
    t6 = <input type="text" placeholder="SEARCH" defaultValue={closet.searchQuery} onChange={t2} onFocus={t3} onBlur={t4} aria-label="Search clothing items" className={t5} />;
    $[4] = closet.searchQuery;
    $[5] = t2;
    $[6] = t3;
    $[7] = t4;
    $[8] = t5;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <svg className="absolute top-1.5 right-0 size-3 text-zinc-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  let t8;
  if ($[11] !== t1 || $[12] !== t6) {
    t8 = <div className="ease-out-strong pointer-events-auto flex justify-end transition-[width,opacity] duration-300 flex-1"><div className={t1}>{t6}{t7}</div></div>;
    $[11] = t1;
    $[12] = t6;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  return t8;
}
function ClosetMobileModeControl(t0) {
  const $ = _c(2);
  const {
    closet
  } = t0;
  if (!closet.isMobile || closet.viewMode !== "closet") {
    return null;
  }
  let t1;
  if ($[0] !== closet) {
    t1 = <div className="fixed top-16 right-0 left-0 z-30 flex justify-center px-4 py-2"><ClosetModeAction closet={closet} /></div>;
    $[0] = closet;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
}
function ClosetMainViews(t0) {
  const $ = _c(31);
  const {
    closet,
    initialOutfits,
    initialOutfitId
  } = t0;
  const t1 = closet.viewMode === "closet" ? "block h-full" : "hidden";
  const t2 = closet.viewMode === "closet";
  let t3;
  if ($[0] !== closet.hoveredCategory || $[1] !== closet.layout || $[2] !== closet.playSound || $[3] !== closet.searchQuery || $[4] !== closet.selectedCategory || $[5] !== closet.setSelectedCategory || $[6] !== closet.soundEnabled || $[7] !== closet.zoom || $[8] !== t2) {
    t3 = <ClosetView searchQuery={closet.searchQuery} selectedCategory={closet.selectedCategory} hoveredCategory={closet.hoveredCategory} onCategoryChange={closet.setSelectedCategory} layout={closet.layout} zoom={closet.zoom} soundEnabled={closet.soundEnabled} playSound={closet.playSound} isVisible={t2} />;
    $[0] = closet.hoveredCategory;
    $[1] = closet.layout;
    $[2] = closet.playSound;
    $[3] = closet.searchQuery;
    $[4] = closet.selectedCategory;
    $[5] = closet.setSelectedCategory;
    $[6] = closet.soundEnabled;
    $[7] = closet.zoom;
    $[8] = t2;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  let t4;
  if ($[10] !== t1 || $[11] !== t3) {
    t4 = <div className={t1}>{t3}</div>;
    $[10] = t1;
    $[11] = t3;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  const t5 = closet.viewMode === "outfits" ? "block h-full" : "hidden";
  let t6;
  if ($[13] !== closet.createOutfit || $[14] !== closet.savedOutfits || $[15] !== closet.viewMode || $[16] !== initialOutfitId || $[17] !== initialOutfits) {
    t6 = closet.viewMode === "outfits" && <LookbookCollection initialOutfits={initialOutfits} savedOutfits={closet.savedOutfits} initialId={initialOutfitId} searchQuery="" onCreate={closet.createOutfit} />;
    $[13] = closet.createOutfit;
    $[14] = closet.savedOutfits;
    $[15] = closet.viewMode;
    $[16] = initialOutfitId;
    $[17] = initialOutfits;
    $[18] = t6;
  } else {
    t6 = $[18];
  }
  let t7;
  if ($[19] !== t5 || $[20] !== t6) {
    t7 = <div className={t5}>{t6}</div>;
    $[19] = t5;
    $[20] = t6;
    $[21] = t7;
  } else {
    t7 = $[21];
  }
  let t8;
  if ($[22] !== closet.handleCreateBack || $[23] !== closet.handleCreateSuccess || $[24] !== closet.playSound || $[25] !== closet.viewMode) {
    t8 = closet.viewMode === "create" && <OutfitBuilder onSuccess={closet.handleCreateSuccess} onBack={closet.handleCreateBack} playSound={closet.playSound} />;
    $[22] = closet.handleCreateBack;
    $[23] = closet.handleCreateSuccess;
    $[24] = closet.playSound;
    $[25] = closet.viewMode;
    $[26] = t8;
  } else {
    t8 = $[26];
  }
  let t9;
  if ($[27] !== t4 || $[28] !== t7 || $[29] !== t8) {
    t9 = <div className="size-full">{t4}{t7}{t8}</div>;
    $[27] = t4;
    $[28] = t7;
    $[29] = t8;
    $[30] = t9;
  } else {
    t9 = $[30];
  }
  return t9;
}
function ClosetBottomControls(t0) {
  const $ = _c(21);
  const {
    closet
  } = t0;
  if (closet.isMobile || closet.viewMode !== "closet") {
    return null;
  }
  let t1;
  if ($[0] !== closet.handleLayoutChange || $[1] !== closet.layout) {
    t1 = <ViewLayoutToggle layout={closet.layout} onLayoutChange={closet.handleLayoutChange} />;
    $[0] = closet.handleLayoutChange;
    $[1] = closet.layout;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== closet.setZoom || $[4] !== closet.zoom) {
    t2 = <ZoomSlider zoom={closet.zoom} onZoomChange={closet.setZoom} />;
    $[3] = closet.setZoom;
    $[4] = closet.zoom;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== t1 || $[7] !== t2) {
    t3 = <div className="flex items-center gap-4">{t1}{t2}</div>;
    $[6] = t1;
    $[7] = t2;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] !== closet.setSoundEnabled || $[10] !== closet.soundEnabled) {
    t4 = <SoundToggle soundEnabled={closet.soundEnabled} onToggle={closet.setSoundEnabled} />;
    $[9] = closet.setSoundEnabled;
    $[10] = closet.soundEnabled;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  let t5;
  if ($[12] !== closet.handleAddClick || $[13] !== closet.viewMode) {
    t5 = closet.viewMode === "closet" && <button type="button" onClick={closet.handleAddClick} className="text-xs opacity-40 transition-opacity hover:opacity-100">ADD FROM URL</button>;
    $[12] = closet.handleAddClick;
    $[13] = closet.viewMode;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  let t6;
  if ($[15] !== t4 || $[16] !== t5) {
    t6 = <div className="flex items-center gap-4">{t4}{t5}</div>;
    $[15] = t4;
    $[16] = t5;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  let t7;
  if ($[18] !== t3 || $[19] !== t6) {
    t7 = <div className="fixed right-8 bottom-8 left-8 z-40 flex items-center justify-between">{t3}{t6}</div>;
    $[18] = t3;
    $[19] = t6;
    $[20] = t7;
  } else {
    t7 = $[20];
  }
  return t7;
}
function ClosetNavigationChrome(t0) {
  const $ = _c(10);
  const {
    closet
  } = t0;
  if (closet.viewMode === "create") {
    return null;
  }
  if (closet.isMobile) {
    const t1 = closet.viewMode === "closet";
    let t2;
    if ($[0] !== closet.handleAddClick || $[1] !== closet.handleLayoutChange || $[2] !== closet.layout || $[3] !== closet.setSoundEnabled || $[4] !== closet.setZoom || $[5] !== closet.soundEnabled || $[6] !== closet.zoom || $[7] !== t1) {
      t2 = <MobileNav collectionControls={t1} layout={closet.layout} onLayoutChange={closet.handleLayoutChange} soundEnabled={closet.soundEnabled} onSoundToggle={closet.setSoundEnabled} zoom={closet.zoom} onZoomChange={closet.setZoom} onAddClick={closet.handleAddClick} />;
      $[0] = closet.handleAddClick;
      $[1] = closet.handleLayoutChange;
      $[2] = closet.layout;
      $[3] = closet.setSoundEnabled;
      $[4] = closet.setZoom;
      $[5] = closet.soundEnabled;
      $[6] = closet.zoom;
      $[7] = t1;
      $[8] = t2;
    } else {
      t2 = $[8];
    }
    return t2;
  }
  let t1;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <AppDock />;
    $[9] = t1;
  } else {
    t1 = $[9];
  }
  return t1;
}
function ClosetPasswordPrompt(t0) {
  const $ = _c(21);
  const {
    closet
  } = t0;
  if (!closet.showPasswordPrompt) {
    return null;
  }
  let t1;
  if ($[0] !== closet.closePasswordPrompt) {
    t1 = <button type="button" onClick={closet.closePasswordPrompt} className="absolute top-4 right-4 text-xs opacity-40 hover:opacity-100">CLOSE</button>;
    $[0] = closet.closePasswordPrompt;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <h2 className="mb-6 text-sm">ENTER PASSWORD</h2>;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== closet) {
    t3 = e => closet.setPassword(e.target.value);
    $[3] = closet;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== closet.password || $[6] !== t3) {
    t4 = <div><input type="password" placeholder="PASSWORD" value={closet.password} onChange={t3} aria-label="Password" className="w-full border-b border-zinc-300 bg-transparent px-2 py-1 text-xs placeholder:text-zinc-400 focus:border-zinc-900 focus:outline-none dark:border-zinc-700 dark:placeholder:text-zinc-600 dark:focus:border-white" required={true} /></div>;
    $[5] = closet.password;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== closet.authError) {
    t5 = closet.authError && <p className="text-xs text-red-600 dark:text-red-400">{closet.authError}</p>;
    $[8] = closet.authError;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <button type="submit" className="flex-1 border border-zinc-900 px-4 py-2 text-xs transition-colors hover:bg-zinc-900 hover:text-white dark:border-white dark:hover:bg-white dark:hover:text-zinc-900">UNLOCK CLOSET</button>;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== closet.closePasswordPrompt) {
    t7 = <div className="flex gap-4 pt-4">{t6}<button type="button" onClick={closet.closePasswordPrompt} className="flex-1 border border-zinc-300 px-4 py-2 text-xs transition-colors hover:bg-zinc-100 dark:border-zinc-700 dark:hover:bg-zinc-800">CANCEL</button></div>;
    $[11] = closet.closePasswordPrompt;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] !== closet.handlePasswordSubmit || $[14] !== t4 || $[15] !== t5 || $[16] !== t7) {
    t8 = <form onSubmit={closet.handlePasswordSubmit} className="space-y-4">{t4}{t5}{t7}</form>;
    $[13] = closet.handlePasswordSubmit;
    $[14] = t4;
    $[15] = t5;
    $[16] = t7;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  let t9;
  if ($[18] !== t1 || $[19] !== t8) {
    t9 = <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-neutral-950/50"><div className="relative w-full max-w-sm bg-white p-6 dark:bg-neutral-950">{t1}{t2}{t8}</div></div>;
    $[18] = t1;
    $[19] = t8;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  return t9;
}